import type { Entity } from '../components'
import type { GameWorld } from '../world'
import { growPlayerFromEating } from '../growth'
import { playSfx } from '../../engine/audio'
import { depthFactor } from '../depth'
import type { EventBus } from '../events'
import { addGraze } from '../graze'
import { addSpurtProgress } from '../growth-spurt'
import { spawnPigmentPuff } from './particles'

// Microbe radius ranges 0.05–0.09, so this yields 1–2 DNA per microbe with
// small ones always worth the minimum.
function dnaForMicrobe(r: number): number {
  return Math.max(1, Math.round(r * 20))
}

export function absorptionSystem(world: GameWorld, player: Entity, dt: number, events: EventBus): void {
  for (const e of world.with('absorbing', 'position', 'renderable')) {
    e.absorbing.progress += dt / e.absorbing.duration

    if (e.absorbing.progress >= 1) {
      let gained = 0
      if (player.dna !== undefined) {
        // Reversal eats carry a fixed payout (same formula as dash kills);
        // microbes go through the radius formula with depth/mutation mults.
        const base = e.absorbing.dnaValue ?? dnaForMicrobe(e.absorbing.startRadius)
        const depth = depthFactor(player)
        const mut = player.mods?.dnaMult ?? 1
        gained = Math.max(1, Math.round(base * (1 + 1.5 * depth) * mut))
        player.dna += gained
        addSpurtProgress(player, gained)
      }
      growPlayerFromEating(player, e.absorbing.growthUnits ?? 1)
      // Overgrazing: only lone microbes graze the cell. Reversal-eaten
      // predators carry dnaValue, and colony critters aren't part of the
      // food-regrowth economy — neither depletes the field.
      if (e.absorbing.dnaValue === undefined && !e.colonyMember) {
        addGraze(e.absorbing.startPos.x, e.absorbing.startPos.y)
      }
      playSfx('eat', { volume: 0.13 })
      events.emit({ type: 'creature.eaten', eater: player, victim: e, gained })
      // Pigment puff at the consumption point — paint scatters as the body
      // is absorbed. Tint comes from the consumed entity's renderable.
      const tint = e.renderable?.tint ?? [0.6, 0.7, 0.9]
      spawnPigmentPuff(world, e.position.x, e.position.y, tint, e.absorbing.startRadius * 0.9)
      world.remove(e)
      continue
    }

    const t = e.absorbing.progress
    // Pull position: ease-in (t²) toward player
    const pullT = t * t
    e.position.x = e.absorbing.startPos.x + (player.position.x - e.absorbing.startPos.x) * pullT
    e.position.y = e.absorbing.startPos.y + (player.position.y - e.absorbing.startPos.y) * pullT

    // Shrink radius: ease-out (1-(1-t)²) so it pops at the end
    const shrinkT = 1 - (1 - t) * (1 - t)
    e.renderable.radius = e.absorbing.startRadius * (1 - shrinkT)
  }
}
